import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Clock, DollarSign, Shield, AlertTriangle, 
  CheckCircle, ChevronLeft, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';

const WithdrawalPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Zap className="w-6 h-6 text-[#00F0FF]" />
            <span className="font-heading font-bold text-lg">MATCHSTAKE</span>
          </div>
          <Button onClick={() => navigate(-1)} variant="outline" className="btn-secondary">
            <ChevronLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[#00F0FF]/20 flex items-center justify-center">
              <FileText className="w-7 h-7 text-[#00F0FF]" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold">Withdrawal Policy</h1>
              <p className="text-white/50">Last updated: March 2025</p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Overview */}
            <section>
              <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#7000FF]" />
                Overview
              </h2>
              <p className="text-white/70 leading-relaxed">
                MatchStake is committed to providing a secure and transparent withdrawal process. 
                To protect our users and maintain platform integrity, all withdrawals undergo 
                administrative review before processing.
              </p>
            </section>

            {/* Key Points */}
            <section className="grid md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <DollarSign className="w-6 h-6 text-[#CCFF00] mb-3" />
                <h3 className="font-semibold mb-1">Minimum Amount</h3>
                <p className="text-2xl font-mono font-bold text-[#CCFF00]">$20</p>
                <p className="text-white/50 text-sm mt-1">Minimum withdrawal</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <DollarSign className="w-6 h-6 text-[#FFB800] mb-3" />
                <h3 className="font-semibold mb-1">Maximum Amount</h3>
                <p className="text-2xl font-mono font-bold text-[#FFB800]">$500</p>
                <p className="text-white/50 text-sm mt-1">Per request</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <Clock className="w-6 h-6 text-[#00F0FF] mb-3" />
                <h3 className="font-semibold mb-1">Processing Time</h3>
                <p className="text-xl font-mono font-bold text-[#00F0FF]">Instant – 24 hrs</p>
                <p className="text-white/50 text-sm mt-1">Depending on approval</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <CheckCircle className="w-6 h-6 text-[#FF2E2E] mb-3" />
                <h3 className="font-semibold mb-1">Withdrawal Fee</h3>
                <p className="text-2xl font-mono font-bold text-[#FF2E2E]">1.5%</p>
                <p className="text-white/50 text-sm mt-1">Max $10 fee cap</p>
              </div>
            </section>

            {/* Detailed Policy */}
            <section>
              <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#7000FF]" />
                Withdrawal Process
              </h2>
              <div className="space-y-4 text-white/70">
                <div className="p-4 rounded-lg bg-white/5">
                  <h3 className="font-semibold text-white mb-2">1. Request Submission</h3>
                  <p>Submit your withdrawal request through the Wallet page. You must have at least $20 in your cash wallet balance. Maximum withdrawal per request is $500.</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5">
                  <h3 className="font-semibold text-white mb-2">2. Fee Structure</h3>
                  <p>A 1.5% withdrawal fee is applied to all withdrawals, with a maximum fee cap of $10. For example, a $100 withdrawal has a fee of $1.50, while a $1000 withdrawal would only have a $10 fee.</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5">
                  <h3 className="font-semibold text-white mb-2">3. Administrative Review</h3>
                  <p>MatchStake reserves the right to review and approve withdrawal requests to prevent fraud or abuse. All withdrawals are reviewed by our administrative team.</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5">
                  <h3 className="font-semibold text-white mb-2">4. Payment Methods</h3>
                  <p>Withdrawals are processed via <strong>Cash App</strong> or <strong>Venmo</strong>. You must provide your correct payment username.</p>
                  <ul className="mt-2 text-sm space-y-1">
                    <li>• <strong>Cash App:</strong> Enter your $Cashtag (e.g., $YourUsername)</li>
                    <li>• <strong>Venmo:</strong> Enter your @username (e.g., @YourUsername)</li>
                  </ul>
                  <p className="mt-3 text-[#FFB800]"><strong>Users are responsible for entering the correct payment username. MatchStake is not responsible for funds sent to incorrect usernames.</strong></p>
                </div>
                <div className="p-4 rounded-lg bg-white/5">
                  <h3 className="font-semibold text-white mb-2">5. Processing Time</h3>
                  <p>Once approved, your withdrawal will be processed from Instant to 24 hours depending on review queue. You will receive a notification when your withdrawal is complete.</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5">
                  <h3 className="font-semibold text-white mb-2">6. Rejection</h3>
                  <p>If a withdrawal is rejected, the funds will be returned to your wallet balance immediately. You will be notified of the reason for rejection.</p>
                </div>
              </div>
            </section>

            {/* Important Notes */}
            <section className="p-4 rounded-xl bg-[#FFB800]/10 border border-[#FFB800]/20">
              <h2 className="font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#FFB800]" />
                Important Notes
              </h2>
              <ul className="text-white/70 space-y-2 text-sm">
                <li>• Withdrawals may be delayed if account activity requires additional verification</li>
                <li>• Suspicious activity may result in account review and withdrawal delays</li>
                <li>• Ensure your payment information is accurate and up-to-date</li>
                <li>• Contact support if your withdrawal takes longer than 72 hours</li>
                <li>• All transactions are subject to review and may require verification before approval</li>
              </ul>
            </section>

            {/* Liability Disclaimer */}
            <section className="p-4 rounded-xl bg-white/5 border border-white/10">
              <h2 className="font-semibold mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#7000FF]" />
                Liability Disclaimer
              </h2>
              <p className="text-white/70 text-sm">
                MatchStake is not responsible for user errors in payment submissions, including incorrect usernames, 
                missing notes, or sending funds to the wrong account. Users must verify their payment details before 
                submitting withdrawal requests. By using our withdrawal service, you acknowledge and accept full 
                responsibility for the accuracy of your payment information.
              </p>
            </section>

            {/* Contact */}
            <section className="text-center pt-6 border-t border-white/10">
              <p className="text-white/50">
                Questions about withdrawals? Contact our support team.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WithdrawalPolicy;
